import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useQueryClient } from 'react-query';
import {
  RiPencilLine,
  RiDeleteBinLine,
  RiAddLine,
  RiErrorWarningLine,
  RiSearchLine,
  RiUploadLine,
  RiDownloadLine,
} from 'react-icons/ri';
import styled from 'styled-components';
import { useCurrentProfile } from '../../hooks/useProfile';
import useWhitelistedDomains, {
  WhitelistedDomain,
} from '../../hooks/useWhitelistedDomains';
import { fadeIn } from '../../styles/animations';
import { PAGINATION_LIMIT } from '../../utils/constants';
import Button from '../components/Button';
import Flex from '../components/Flex';
import Modal from '../components/Modal';
import { Pagination, Table, TBody, TD, TH, THead, TR } from '../components/Table';
import Toast from '../components/Toast';
import { Heading3 } from '../components/Typography';
import Input from '../components/Input';
import Select, { Option } from '../components/Select';
import ToggleSwitch from '../components/ToggleSwitch';

type FormMode = 'create' | 'edit';

const emptyForm = {
  domain: '',
  description: '',
  is_active: true,
  client_name: '',
  email_provider: '',
  email_sender: '',
  support_email: '',
  client_id: '',
};

const WhitelistedDomains = () => {
  const { data: currentProfile } = useCurrentProfile();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | boolean>('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>('create');
  const [editingDomain, setEditingDomain] = useState<WhitelistedDomain | null>(
    null
  );
  const [deletingDomain, setDeletingDomain] =
    useState<WhitelistedDomain | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState<{
    domain?: string;
    description?: string;
    client_name?: string;
    email_provider?: string;
    email_sender?: string;
    support_email?: string;
    client_id?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastSuccess, setToastSuccess] = useState(false);
  const [toastText, setToastText] = useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, showDeleted]);

  const {
    isLoading,
    isSuccess,
    data,
    isError,
    createDomain,
    updateDomain,
    deleteDomain,
    toggleStatus,
    importFromCSV,
    isCreating,
    isUpdating,
    isDeleting,
    isTogglingStatus,
    isImportingCSV,
  } = useWhitelistedDomains({
    page,
    limit: PAGINATION_LIMIT,
    search,
    is_active: statusFilter,
    include_deleted: showDeleted,
  });

  const domains = data?.domains || [];
  const paginationData = data?.pagination_data;

  const isAdmin = useMemo(
    () =>
      currentProfile?.user_role === 'admin' ||
      currentProfile?.user_role === 'superadmin',
    [currentProfile?.user_role]
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const openCreateModal = () => {
    setFormMode('create');
    setEditingDomain(null);
    setForm(emptyForm);
    setFormErrors({});
    setShowModal(true);
  };

  const openEditModal = (domain: WhitelistedDomain) => {
    setFormMode('edit');
    setEditingDomain(domain);
    setForm({
      domain: domain.domain || '',
      description: domain.description || '',
      is_active: domain.is_active ?? true,
      client_name: domain.client_name || '',
      email_provider: domain.email_provider?.toString() || '',
      email_sender: domain.email_sender || '',
      support_email: domain.support_email || '',
      client_id: domain.client_id || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDownloadSampleCSV = () => {
    const sampleCSV = `domain,description,is_active,client_name,email_provider,email_sender,support_email,client_id
example.com,Example domain for testing,true,Example Client,1,info@example.com,support@example.com,client_123
test.com,Test domain,true,Test Client,1,test@test.com,support@test.com,client_456
demo.org,Demo domain,false,Demo Client,1,demo@demo.org,support@demo.org,client_789`;
    
    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'whitelist_domains_sample.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    setToastText('Sample CSV file downloaded');
    setToastSuccess(true);
    setShowToast(true);
  };

  const handleCSVUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleCSVFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setToastText('Please select a CSV file');
      setToastSuccess(false);
      setShowToast(true);
      e.target.value = '';
      return;
    }

    try {
      const result = await importFromCSV(file);
      
      const { created, skipped, errors } = result;
      let message = `Import completed: ${created} domain(s) created`;
      if (skipped > 0) message += `, ${skipped} skipped`;
      if (errors > 0) message += `, ${errors} error(s)`;
      
      setToastText(message);
      setToastSuccess(true);
      setShowToast(true);
      
      // Show error details if any
      if (errors > 0 && result.error_details?.length > 0) {
        const errorList = result.error_details
          .map((e) => `Row ${e.row} (${e.domain}): ${e.error}`)
          .join('\n');
        setTimeout(() => {
          setToastText(`Errors:\n${errorList}`);
          setToastSuccess(false);
          setShowToast(true);
        }, 3000);
      }
    } catch (error: any) {
      setToastText(error?.message || 'Failed to import domains from CSV');
      setToastSuccess(false);
      setShowToast(true);
    } finally {
      // Reset input
      e.target.value = '';
    }
  };

  const openDeleteModal = (domain: WhitelistedDomain) => {
    setDeletingDomain(domain);
    setShowDeleteModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDomain(null);
    setForm(emptyForm);
    setFormErrors({});
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingDomain(null);
  };

  const setToast = (success: boolean, message: string) => {
    setToastSuccess(success);
    setToastText(message);
    setShowToast(true);
  };

  const handleChange = (field: keyof typeof form, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: { domain?: string; description?: string } = {};

    if (!form.domain.trim()) {
      errors.domain = 'Domain is required';
    }

    if (form.description && form.description.length > 500) {
      errors.description = 'Description must be 500 characters or less';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const payload: {
        domain: string;
        description?: string;
        is_active?: boolean;
        client_name?: string;
        email_provider?: number;
        email_sender?: string;
        support_email?: string;
        client_id?: string;
      } = {
        domain: form.domain.trim(),
        is_active: form.is_active,
      };

      if (form.description.trim()) {
        payload.description = form.description.trim();
      }
      if (form.client_name?.trim()) {
        payload.client_name = form.client_name.trim();
      }
      if (form.email_provider?.trim()) {
        const providerId = parseInt(form.email_provider.trim(), 10);
        if (!isNaN(providerId)) {
          payload.email_provider = providerId;
        }
      }
      if (form.email_sender?.trim()) {
        payload.email_sender = form.email_sender.trim();
      }
      if (form.support_email?.trim()) {
        payload.support_email = form.support_email.trim();
      }
      if (form.client_id?.trim()) {
        payload.client_id = form.client_id.trim();
      }

      if (formMode === 'create') {
        await createDomain(payload);
        setToast(true, 'Domain added successfully');
      } else if (editingDomain) {
        await updateDomain({ id: editingDomain.id, payload });
        setToast(true, 'Domain updated successfully');
      }
      closeModal();
      queryClient.invalidateQueries(['whitelisted_domains']);
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        error?.response?.data?.error?.[0]?.form_level_error ||
        'Something went wrong while saving the domain.';
      setToast(false, errorMessage);

      // Set field-level error if it's a domain-related error
      if (
        errorMessage.toLowerCase().includes('domain') &&
        errorMessage.toLowerCase().includes('exists')
      ) {
        setFormErrors({ domain: 'This domain is already whitelisted' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (permanent: boolean = false) => {
    if (!isAdmin || !deletingDomain) return;

    try {
      setIsSubmitting(true);
      await deleteDomain(deletingDomain.id, permanent);
      setToast(
        true,
        permanent
          ? 'Domain permanently deleted'
          : 'Domain deleted successfully'
      );
      closeDeleteModal();
      queryClient.invalidateQueries(['whitelisted_domains']);
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        error?.response?.data?.error?.[0]?.form_level_error ||
        'Something went wrong while deleting the domain.';
      setToast(false, errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (domain: WhitelistedDomain) => {
    if (!isAdmin) return;

    try {
      await toggleStatus({ id: domain.id, is_active: !domain.is_active });
      setToast(true, `Domain ${!domain.is_active ? 'activated' : 'deactivated'} successfully`);
      queryClient.invalidateQueries(['whitelisted_domains']);
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        error?.response?.data?.error?.[0]?.form_level_error ||
        'Something went wrong while updating the domain status.';
      setToast(false, errorMessage);
    }
  };

  const getStatusFilterLabel = () => {
    if (statusFilter === true || statusFilter === 'true') return 'Active';
    if (statusFilter === false || statusFilter === 'false') return 'Inactive';
    return 'All Status';
  };

  const renderContent = () => {
    if (!isAdmin) {
      return (
        <ForbiddenContainer column align="center" justify="center">
          <Heading3>Access restricted</Heading3>
          <ForbiddenText>
            This section is only available to admin users.
          </ForbiddenText>
        </ForbiddenContainer>
      );
    }

    if (isLoading) {
      return (
        <TR>
          <TD colSpan={9} centered>
            Loading whitelisted domains...
          </TD>
        </TR>
      );
    }

    if (isError) {
      return (
        <TR>
          <TD colSpan={9} centered>
            Failed to load whitelisted domains.
          </TD>
        </TR>
      );
    }

    if (isSuccess && (!domains || domains.length === 0)) {
      return (
        <TR>
          <TD colSpan={9} centered>
            No whitelisted domains found. Add one to get started.
          </TD>
        </TR>
      );
    }

    const isDeleted = (domain: WhitelistedDomain) => domain.deleted_at !== null;

    return (
      domains &&
      domains.map((domain) => {
        const deleted = isDeleted(domain);
        return (
          <StyledTR key={domain.id} style={{ opacity: deleted ? 0.7 : 1 }}>
            <TD maxWidth="15%" style={{ paddingLeft: '4rem' }}>
              {domain.domain}
              {deleted && (
                <DeletedBadge>Deleted</DeletedBadge>
              )}
            </TD>
            <TD maxWidth="20%">{domain.description || '-'}</TD>
            <TD maxWidth="12%">{domain.client_name || '-'}</TD>
            <TD maxWidth="10%">{domain.email_provider || '-'}</TD>
            <TD maxWidth="12%">{domain.email_sender || '-'}</TD>
            <TD maxWidth="12%">{domain.support_email || '-'}</TD>
            <TD maxWidth="12%">{domain.client_id || '-'}</TD>
            <TD maxWidth="10%">
              {deleted ? (
                <DeletedStatusBadge>Deleted</DeletedStatusBadge>
              ) : (
                <StatusBadge $isActive={domain.is_active}>
                  {domain.is_active ? 'Active' : 'Inactive'}
                </StatusBadge>
              )}
            </TD>
            <TD maxWidth="12%">{formatDate(domain.created_at)}</TD>
            <TD>
              <ActionsContainer>
                {!deleted && (
                  <StatusToggleContainer
                    onClick={() => !isTogglingStatus && handleToggleStatus(domain)}
                    $disabled={isTogglingStatus}
                    title={domain.is_active ? 'Deactivate' : 'Activate'}
                  >
                    <ToggleSwitch
                      labels={['Inactive', 'Active']}
                      checked={domain.is_active}
                      onClick={() => {}}
                    />
                  </StatusToggleContainer>
                )}
                {!deleted && (
                  <IconButton
                    type="button"
                    onClick={() => openEditModal(domain)}
                    disabled={!isAdmin}
                    title="Edit domain"
                  >
                    <RiPencilLine />
                  </IconButton>
                )}
                <IconButton
                  type="button"
                  onClick={() => openDeleteModal(domain)}
                  disabled={!isAdmin || isSubmitting}
                  title={deleted ? 'Permanently delete domain' : 'Delete domain'}
                >
                  <RiDeleteBinLine />
                </IconButton>
              </ActionsContainer>
            </TD>
          </StyledTR>
        );
      })
    );
  };

  return (
    <Container column>
      <Header align="center" justify="space-between">
        <HeaderContent column>
          <Heading3>Whitelisted Domains</Heading3>
          <Subtitle>Manage domains allowed for OIDC redirects</Subtitle>
        </HeaderContent>
        {isAdmin && (
          <Flex align="center" style={{ gap: '0.8rem' , width: '40%', justifyContent: 'flex-end'}}>
            <Button
              onClick={handleDownloadSampleCSV}
              style={{
                backgroundColor: '#f0f0f0',
                border: '1px solid #d9d9d9',
                color: '#595959',
                height: '4rem',
                padding: '0 0.5rem',
                fontWeight: '500',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e6e6e6';
                e.currentTarget.style.borderColor = '#bfbfbf';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.borderColor = '#d9d9d9';
              }}
            >
              <RiDownloadLine style={{ marginRight: '0.5rem', fontSize: '1.6rem' }} />
              Sample CSV
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleCSVFileChange}
              disabled={isImportingCSV}
            />
            <Button
              onClick={handleCSVUploadClick}
              style={{
                backgroundColor: '#3f52fd',
                border: '1px solid #3f52fd',
                color: '#ffffff',
                height: '4rem',
                padding: '0 1.5rem',
                fontWeight: '500',
                cursor: isImportingCSV ? 'not-allowed' : 'pointer',
                opacity: isImportingCSV ? 0.6 : 1,
                transition: 'all 0.3s ease',
              }}
              disabled={isImportingCSV}
              onMouseEnter={(e) => {
                if (!isImportingCSV) {
                  e.currentTarget.style.backgroundColor = '#2d3ed1';
                  e.currentTarget.style.borderColor = '#2d3ed1';
                }
              }}
              onMouseLeave={(e) => {
                if (!isImportingCSV) {
                  e.currentTarget.style.backgroundColor = '#3f52fd';
                  e.currentTarget.style.borderColor = '#3f52fd';
                }
              }}
            >
              <RiUploadLine style={{ marginRight: '0.5rem', fontSize: '1.6rem' }} />
              {isImportingCSV ? 'Importing...' : 'Import from CSV'}
            </Button>
            <AddDomainButton onClick={openCreateModal}>
              <RiAddLine style={{ marginRight: '0.5rem' }} />
              Add Domain
            </AddDomainButton>
          </Flex>
        )}
      </Header>

      <FiltersContainer align="center" justify="space-between">
        <SearchContainer>
          <SearchIcon>
            <RiSearchLine />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search domains..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchContainer>
        <FilterContainer style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <Select value={getStatusFilterLabel()}>
            <Option onClick={() => setStatusFilter('')}>All Status</Option>
            <Option onClick={() => setStatusFilter(true)}>Active</Option>
            <Option onClick={() => setStatusFilter(false)}>Inactive</Option>
          </Select>
          <Button
            type="button"
            onClick={() => setShowDeleted(!showDeleted)}
            style={{
              backgroundColor: showDeleted ? '#3f52fd' : 'transparent',
              border: '1px solid #3f52fd',
              color: showDeleted ? '#ffffff' : '#3f52fd',
              height: '3.6rem',
              padding: '0 1.6rem',
              fontSize: '1.3rem',
            }}
          >
            {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
          </Button>
        </FilterContainer>
      </FiltersContainer>

      <Table>
        <THead>
          <StyledTR>
            <TH style={{ paddingLeft: '4rem' }}>Domain</TH>
            <TH>Description</TH>
            <TH>Client Name</TH>
            <TH>Email Provider</TH>
            <TH>Email Sender</TH>
            <TH>Support Email</TH>
            <TH>Client ID</TH>
            <TH>Status</TH>
            <TH>Created</TH>
            <TH>Actions</TH>
          </StyledTR>
        </THead>
        <TBody>{renderContent()}</TBody>
      </Table>

      {paginationData && !isLoading && (
        <Pagination
          currentPage={paginationData.page_no}
          nextPageChangeHandler={
            paginationData.page_next
              ? () => setPage((prev) => prev + 1)
              : undefined
          }
          prevPageChangeHandler={
            paginationData.page_prev
              ? () => setPage((prev) => prev - 1)
              : undefined
          }
        />
      )}

      {showModal && (
        <Modal>
          <FormContainer column as="form" onSubmit={handleSubmit}>
            <Heading3>
              {formMode === 'create' ? 'Add New Domain' : 'Edit Domain'}
            </Heading3>
            <FormFields column>
              <Input
                label="Domain"
                placeholder="example.com or https://example.com"
                value={form.domain}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('domain', e.target.value)
                }
                error={formErrors.domain}
                required
              />
              <DomainHint>
                Enter domain (e.g., example.com or https://www.example.com)
              </DomainHint>
              <TextareaContainer>
                <TextareaLabel>
                  Description <span>(optional)</span>
                </TextareaLabel>
                <StyledTextarea
                  placeholder="Optional description for this domain"
                  value={form.description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    handleChange('description', e.target.value)
                  }
                  rows={4}
                  maxLength={500}
                />
                {formErrors.description && (
                  <ErrorMessage>{formErrors.description}</ErrorMessage>
                )}
                <CharCount>
                  {form.description.length}/500 characters
                </CharCount>
              </TextareaContainer>
              <Input
                label="Client Name"
                placeholder="Client name (optional)"
                value={form.client_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('client_name', e.target.value)
                }
                error={formErrors.client_name}
              />
              <Input
                label="Email Provider"
                placeholder="Email provider ID (optional)"
                type="number"
                value={form.email_provider}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('email_provider', e.target.value)
                }
                error={formErrors.email_provider}
              />
              <Input
                label="Email Sender"
                placeholder="Email sender address (optional)"
                value={form.email_sender}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('email_sender', e.target.value)
                }
                error={formErrors.email_sender}
              />
              <Input
                label="Support Email"
                placeholder="Support email address (optional)"
                value={form.support_email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('support_email', e.target.value)
                }
                error={formErrors.support_email}
              />
              <Input
                label="Client ID"
                placeholder="Client ID (optional)"
                value={form.client_id}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('client_id', e.target.value)
                }
                error={formErrors.client_id}
              />
              <CheckboxContainer>
                <CheckboxLabel>
                  <CheckboxInput
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                  />
                  <CheckboxText>Active</CheckboxText>
                </CheckboxLabel>
                <CheckboxHint>
                  Active domains are allowed for OIDC redirects
                </CheckboxHint>
              </CheckboxContainer>
            </FormFields>
            <FormActions justify="flex-end">
              <Button
                type="button"
                variant="secondary"
                onClick={closeModal}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <PrimaryButton type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? formMode === 'create'
                    ? 'Adding...'
                    : 'Saving...'
                  : formMode === 'create'
                  ? 'Add Domain'
                  : 'Save Changes'}
              </PrimaryButton>
            </FormActions>
          </FormContainer>
        </Modal>
      )}

      {showDeleteModal && deletingDomain && (
        <Modal>
          <DeleteModalContainer column>
            <WarningIcon>
              <RiErrorWarningLine />
            </WarningIcon>
            <DeleteTitle>Delete Domain?</DeleteTitle>
            <DeleteMessage>
              {deletingDomain.deleted_at ? (
                <>
                  Are you sure you want to <strong>permanently delete</strong>{' '}
                  <DomainName>{deletingDomain.domain}</DomainName>? This action
                  cannot be undone and will remove the domain from the database.
                </>
              ) : (
                <>
                  Are you sure you want to delete{' '}
                  <DomainName>{deletingDomain.domain}</DomainName>?
                </>
              )}
            </DeleteMessage>
            <DeleteOptionsContainer column>
              {!deletingDomain.deleted_at && (
                <DeleteOptionText>
                  Choose deletion type:
                </DeleteOptionText>
              )}
              <DeleteActions justify="flex-end" style={{ gap: '1rem', marginTop: '1.6rem' }}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeDeleteModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                {!deletingDomain.deleted_at && (
                  <Button
                    type="button"
                    onClick={() => handleDelete(false)}
                    disabled={isSubmitting}
                    style={{
                      backgroundColor: '#ff9800',
                      border: '1px solid #ff9800',
                      color: '#ffffff',
                    }}
                  >
                    {isSubmitting ? 'Deleting...' : 'Delete'}
                  </Button>
                )}
                <DeleteButton
                  type="button"
                  onClick={() => handleDelete(true)}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Deleting...' : deletingDomain.deleted_at ? 'Permanently Delete' : 'Permanent Delete'}
                </DeleteButton>
              </DeleteActions>
            </DeleteOptionsContainer>
          </DeleteModalContainer>
        </Modal>
      )}

      {showToast && (
        <Toast
          success={toastSuccess}
          onClose={(visible: boolean) => setShowToast(visible)}
        >
          {toastText}
        </Toast>
      )}
    </Container>
  );
};

export default WhitelistedDomains;

const Container = styled(Flex)`
  width: 100%;
  animation: ${fadeIn} 0.4s;
`;

const Header = styled(Flex)`
  width: 100%;
  padding: 1rem 4rem;
  border-top: 1px solid ${(props) => props.theme.colors.grey[200]};
  border-bottom: 1px ridge ${(props) => props.theme.colors.grey[200]};
  background-color: #fff;
`;

const HeaderContent = styled(Flex)`
  gap: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1.3rem;
  color: ${(props) => props.theme.colors.grey[400]};
  margin: 0;
`;

const FiltersContainer = styled(Flex)`
  width: 100%;
  padding: 1.5rem 4rem;
  background-color: #fff;
  border-bottom: 1px solid ${(props) => props.theme.colors.grey[200]};
  gap: 2rem;
`;

const SearchContainer = styled(Flex)`
  position: relative;
  flex: 1;
  max-width: 40rem;
  align-items: center;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1.2rem;
  font-size: 1.8rem;
  color: ${(props) => props.theme.colors.grey[400]};
  z-index: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 4rem;
  padding: 0 1.2rem 0 4rem;
  border-radius: 4px;
  border: 2px solid ${(props) => props.theme.colors.grey[300]};
  font-size: 1.4rem;
  font-family: 'Poppins';
  outline: none;

  &:focus {
    border-color: ${(props) => props.theme.colors.blue[500]};
    box-shadow: 0 0 2px ${(props) => props.theme.colors.blue[500]};
  }
`;

const FilterContainer = styled.div`
  min-width: 15rem;
`;

const StyledTR = styled(TR)`
  border-bottom: 1px solid ${(props) => props.theme.colors.grey[200]};
`;

const DeletedBadge = styled.span`
  display: inline-block;
  margin-left: 0.8rem;
  padding: 0.2rem 0.6rem;
  border-radius: 0.8rem;
  font-size: 1rem;
  font-weight: 500;
  background-color: #ff4d4f;
  color: #ffffff;
`;

const DeletedStatusBadge = styled.span`
  display: inline-block;
  padding: 0.4rem 1rem;
  border-radius: 1.2rem;
  font-size: 1.2rem;
  font-weight: 500;
  background-color: #ff4d4f;
  color: #ffffff;
`;

const StatusBadge = styled.span<{ $isActive: boolean }>`
  display: inline-block;
  padding: 0.4rem 1rem;
  border-radius: 1.2rem;
  font-size: 1.2rem;
  font-weight: 500;
  background-color: ${(props) =>
    props.$isActive
      ? props.theme.colors.green[200] || '#D7EBD3'
      : props.theme.colors.grey[200]};
  color: ${(props) =>
    props.$isActive
      ? props.theme.colors.green[500] || '#5BC044'
      : props.theme.colors.grey[500]};
`;

const AddDomainButton = styled(Button)`
  min-width: 16rem;
  width: auto;
  height: 4rem;
  white-space: nowrap;
  padding: 0 2.4rem;
  font-weight: 500;
`;

const ActionsContainer = styled(Flex)`
  gap: 1rem;
  align-items: center;
`;

const StatusToggleContainer = styled.div<{ $disabled: boolean }>`
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  padding: 0.5rem;

  &:hover:not([disabled]) {
    opacity: 0.8;
  }
`;

const IconButton = styled.button`
  outline: none;
  border: none;
  height: fit-content;
  padding: 0.5rem;
  padding-bottom: 0;
  border-radius: 4px;
  background: none;
  font-size: 2rem;
  cursor: pointer;
  color: ${(props) => props.theme.colors.grey[600]};

  &:hover {
    background-color: ${(props) => props.theme.colors.grey[100]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormContainer = styled(Flex)`
  width: 42rem;
  max-height: 80vh;
  padding: 2.4rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 0.4rem 1.2rem rgba(0, 0, 0, 0.08);
  gap: 1.6rem;
  overflow-y: auto;
`;

const FormFields = styled(Flex)`
  width: 100%;
  gap: 1.5rem;
`;

const FormActions = styled(Flex)`
  width: 100%;
  gap: 1rem;
`;

const PrimaryButton = styled(Button)`
  width: 10rem;
  height: 4rem;
`;

const ForbiddenContainer = styled(Flex)`
  width: 100%;
  height: calc(100vh - 7rem);
  animation: ${fadeIn} 0.4s;
`;

const ForbiddenText = styled.p`
  margin-top: 1rem;
  font-size: 1.4rem;
  color: ${(props) => props.theme.colors.grey[400]};
`;

const DomainHint = styled.p`
  margin: -1rem 0 0 0;
  font-size: 1.2rem;
  color: ${(props) => props.theme.colors.grey[400]};
  font-style: italic;
`;

const TextareaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const TextareaLabel = styled.label`
  font-size: 1.3rem;
  font-weight: 500;
  color: ${(props) => props.theme.colors.grey[500]};

  span {
    font-weight: 400;
    color: ${(props) => props.theme.colors.grey[400]};
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 1.2rem;
  margin-top: 0.5rem;
  border-radius: 4px;
  border: 2px solid ${(props) => props.theme.colors.grey[300]};
  font-size: 1.4rem;
  font-family: 'Poppins';
  outline: none;
  resize: vertical;
  min-height: 8rem;

  &:focus {
    border-color: ${(props) => props.theme.colors.blue[500]};
    box-shadow: 0 0 2px ${(props) => props.theme.colors.blue[500]};
  }
`;

const CharCount = styled.span`
  font-size: 1.2rem;
  color: ${(props) => props.theme.colors.grey[400]};
  text-align: right;
`;

const ErrorMessage = styled.span`
  font-size: 1.2rem;
  color: ${(props) => props.theme.colors.red[500]};
  margin-top: 0.4rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
`;

const CheckboxInput = styled.input`
  width: 2rem;
  height: 2rem;
  cursor: pointer;
  accent-color: ${(props) => props.theme.colors.blue[500]};
`;

const CheckboxText = styled.span`
  font-size: 1.4rem;
  font-weight: 500;
  color: ${(props) => props.theme.colors.grey[600]};
`;

const CheckboxHint = styled.p`
  margin: 0;
  font-size: 1.2rem;
  color: ${(props) => props.theme.colors.grey[400]};
  font-style: italic;
`;

const DeleteModalContainer = styled(Flex)`
  width: 42rem;
  padding: 2.4rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 0.4rem 1.2rem rgba(0, 0, 0, 0.08);
  gap: 1.6rem;
  align-items: center;
`;

const WarningIcon = styled.div`
  font-size: 4rem;
  color: ${(props) => props.theme.colors.orange || '#F2994A'};
  margin-bottom: 0.5rem;
`;

const DeleteTitle = styled(Heading3)`
  text-align: center;
  margin: 0;
`;

const DeleteMessage = styled.p`
  font-size: 1.4rem;
  color: ${(props) => props.theme.colors.grey[600]};
  text-align: center;
  line-height: 1.6;
  margin: 0;
`;

const DomainName = styled.strong`
  color: ${(props) => props.theme.colors.grey[600]};
  font-weight: 600;
`;

const DeleteOptionsContainer = styled(Flex)`
  width: 100%;
  margin-top: 1.6rem;
`;

const DeleteOptionText = styled.p`
  font-size: 1.3rem;
  color: ${(props) => props.theme.colors.grey[500]};
  margin: 0;
  font-weight: 500;
`;

const DeleteActions = styled(Flex)`
  width: 100%;
  gap: 1rem;
  margin-top: 1rem;
`;

const DeleteButton = styled(Button)`
  min-width: 14rem;
  height: 4rem;
  background-color: ${(props) => props.theme.colors.red[500]};
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: ${(props) => props.theme.colors.red[400]};
  }
`;
